import { useCallback, useMemo, useState } from 'react';

import type { ConnectionSectionsData } from './useConnectionSectionsData.ts';

/** The stable identity of each connection section, reused as its expansion key. */
export type SectionKey = keyof ConnectionSectionsData;

export type SectionExpansionController = {
	/** Whether the given section is currently expanded. Defaults to collapsed. */
	isExpanded: (section: SectionKey) => boolean;
	/** Flips the given section between expanded and collapsed. */
	toggle: (section: SectionKey) => void;
};

export type UseSectionExpansionResult = SectionExpansionController & {
	/** Collapses every section again. */
	reset: () => void;
};

/**
 * The controlled-expansion pair a section forwards to its shell. Section groups
 * fill these in from the modal's controller; the panel and popup omit them,
 * so the shell falls back to owning its own state.
 */
export type SectionExpansionProps = {
	/** Controlled expanded state. Omit to let the section's shell own it. */
	isExpanded?: boolean;
	/** Called on a header click when `isExpanded` is controlled. */
	onToggleExpanded?: () => void;
};

export const getSectionExpansionProps = (
	controller: SectionExpansionController | undefined,
	section: SectionKey,
): SectionExpansionProps =>
	controller
		? {
				isExpanded: controller.isExpanded(section),
				onToggleExpanded: () => controller.toggle(section),
			}
		: {};

/**
 * Holds which connection sections are expanded, lifted out of the sections themselves so
 * the state survives the Teamwork Graph modal swapping its right column between the
 * connections and Rovo chat. That swap unmounts `TeamworkGraphContextColumn` and every
 * section under it, so a section that owned its own expanded state would reset to
 * collapsed on the way back from Rovo — which is the bug this hook exists to fix.
 *
 * Only the modal needs this: the preview panel and the byline popup keep their sections
 * mounted, so those surfaces leave the shell uncontrolled and let it own the state.
 */
export const useSectionExpansion = (): UseSectionExpansionResult => {
	// The set holds exactly the expanded sections; everything else is collapsed.
	const [expanded, setExpanded] = useState<ReadonlySet<SectionKey>>(() => new Set());

	const isExpanded = useCallback((section: SectionKey) => expanded.has(section), [expanded]);

	const toggle = useCallback((section: SectionKey) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (!next.delete(section)) {
				next.add(section);
			}
			return next;
		});
	}, []);

	const reset = useCallback(() => {
		setExpanded(new Set());
	}, []);

	return useMemo(() => ({ isExpanded, toggle, reset }), [isExpanded, toggle, reset]);
};
